import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const Sidebar = ({ currentSection, onNavigate, onCollapseChange, user, onLogout }) => {
  // Collapse state management with localStorage persistence
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Company/clinic selector state
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentClinic, setCurrentClinic] = useState(() => {
    return user?.companyName || user?.company?.name || 'Harley Street Wellness Centre';
  });

  // Save collapse state to localStorage and notify parent
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed.toString());
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
  }, [isCollapsed, onCollapseChange]);

  // Update clinic name when user changes
  useEffect(() => {
    if (user?.companyName || user?.company?.name) {
      setCurrentClinic(user.companyName || user.company.name);
    }
  }, [user]);

  // Fetch companies and locations from API
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return; // Only fetch when user is authenticated

      setLoading(true);
      try {
        const [companiesResponse, locationsResponse] = await Promise.all([
          apiService.getCompanies().catch(() => ({ success: false, data: [] })),
          apiService.getOffices().catch(() => ({ success: false, data: [] }))
        ]);

        if (companiesResponse.success !== false && Array.isArray(companiesResponse)) {
          setCompanies(companiesResponse);
        } else if (companiesResponse.data && Array.isArray(companiesResponse.data)) {
          setCompanies(companiesResponse.data);
        }

        if (locationsResponse.success !== false && Array.isArray(locationsResponse)) {
          setLocations(locationsResponse);
        } else if (locationsResponse.data && Array.isArray(locationsResponse.data)) {
          setLocations(locationsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        // Don't show error to user - the current user's company will be shown as fallback
        // This is expected if user doesn't have admin permissions to access companies/locations
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Click-outside detection for dropdowns and mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close clinic dropdown
      if (!event.target.closest('.company-branding') && !event.target.closest('.clinic-dropdown')) {
        setShowDropdown(false);
      }
      // Close account dropdown
      if (!event.target.closest('.user-profile') && !event.target.closest('.account-dropdown')) {
        setShowAccountDropdown(false);
      }
      // Close mobile menu if clicking outside sidebar
      if (isMobileMenuOpen && !event.target.closest('.sidebar') && !event.target.closest('.mobile-menu-toggle')) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  // API data state
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Combined options for dropdown (companies + locations)
  const clinicOptions = [
    // Add current user's company first if available
    ...(user?.companyName ? [{
      type: 'company',
      id: user.companyId || 'current',
      name: user.companyName,
      displayName: user.companyName
    }] : []),
    // Add companies from API
    ...companies.map(company => ({
      type: 'company',
      id: company.id,
      name: company.name || company.displayName,
      displayName: company.displayName || company.name
    })),
    // Add locations from API
    ...locations.map(location => ({
      type: 'location',
      id: location.id,
      name: location.name,
      address: location.address || location.addressLine1
    }))
  ];

  // Toggle function
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Simple dropdown toggle
  const toggleDropdown = () => setShowDropdown(!showDropdown);

  // Handle clinic selection
  const selectClinic = (clinic) => {
    setCurrentClinic(clinic);
    setShowDropdown(false);
  };

  // Get page title from current section
  const getPageTitle = () => {
    const titleMap = {
      'dashboard': 'Dashboard',
      'calendar': 'Master Calendar',
      'bookings': 'All Bookings',
      'customers': 'Customer Database',
      'practitioners': 'Practitioners',
      'locations': 'Locations',
      'services': 'Services',
      'widgets': 'Widget Manager',
      'integrations': 'Integrations',
      'user-management': 'User Management',
      'roles-permissions': 'Roles & Permissions',
      'settings': 'Settings'
    };
    return titleMap[currentSection] || 'Dashboard';
  };

  // Account dropdown state
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);

  // Handle account dropdown toggle
  const handleAccountToggle = () => {
    setShowAccountDropdown(!showAccountDropdown);
  };

  // Handle navigation with mobile menu close
  const handleNavigation = (sectionId) => {
    onNavigate(sectionId);
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };


  const navigationSections = [
    {
      title: null, // No title for Overview section
      items: [
        { id: 'dashboard', label: 'Dashboard', iconClass: 'icon-dashboard', badge: null },
        { id: 'calendar', label: 'Master Calendar', iconClass: 'icon-calendar', badge: 15 },
        { id: 'bookings', label: 'All Bookings', iconClass: 'icon-bookings', badge: null },
        { id: 'customers', label: 'Customer Database', iconClass: 'icon-customers', badge: null }
      ]
    },
    {
      title: 'Practice Management',
      items: [
        { id: 'practitioners', label: 'Practitioners', iconClass: 'icon-practitioners', badge: null },
        { id: 'locations', label: 'Locations', iconClass: 'icon-locations', badge: null },
        { id: 'services', label: 'Services', iconClass: 'icon-services', badge: null }
      ]
    },
    {
      title: 'Digital Experience',
      items: [
        { id: 'widgets', label: 'Widget Manager', iconClass: 'icon-widgets', badge: null },
        { id: 'integrations', label: 'Integrations', iconClass: 'icon-integrations', badge: null }
      ]
    },
    {
      title: 'System',
      items: [
        { id: 'user-management', label: 'User Management', iconClass: 'icon-user-management', badge: null },
        { id: 'roles-permissions', label: 'Roles & Permissions', iconClass: 'icon-roles-permissions', badge: null },
        { id: 'settings', label: 'Settings', iconClass: 'icon-settings', badge: null }
      ]
    }
  ];

  return (
    <>
      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && <div className="mobile-menu-backdrop" onClick={() => setIsMobileMenuOpen(false)} />}

      {/* Mobile Menu Toggle Button */}
      <button
        className="mobile-menu-toggle"
        onClick={toggleMobileMenu}
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
      >
        <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
        <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
        <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
      </button>

      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
      {/* Modern Floating Grip Handle */}
      <button
        className="sidebar-grip"
        onClick={toggleSidebar}
        aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
        title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
      >
        {/* Grip lines handled by CSS ::before and ::after pseudo-elements */}
      </button>

      <div className={`brand ${isCollapsed ? 'collapsed' : ''}`}>
        <div
          className="company-branding"
          onClick={toggleDropdown}
        >
          {/* Company Logo/Initial */}
          <div className="company-logo">
            <span className="company-initial">
              {currentClinic.split(' ').map(word => word[0]).join('').substring(0, 2)}
            </span>
          </div>

          {/* Company Name - Only shown when expanded */}
          {!isCollapsed && (
            <div className="company-info">
              <span className="company-name">{currentClinic}</span>
            </div>
          )}

        </div>

        {/* ALWAYS RENDER - NEVER CONDITIONAL */}
        <div
          className="clinic-dropdown"
          style={{
            display: showDropdown ? 'block' : 'none',
            position: 'fixed',
            top: '80px',
            left: isCollapsed ? '90px' : '20px',
            backgroundColor: '#ffffff',
            opacity: 1,
            zIndex: 99999,
            width: '320px',
            minWidth: '320px',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        >
          {loading ? (
            <div className="clinic-option loading">
              Loading...
            </div>
          ) : clinicOptions.length > 0 ? (
            clinicOptions.map((option) => (
              <button
                key={`${option.type}-${option.id}`}
                className={`clinic-option ${option.name === currentClinic ? 'active' : ''}`}
                onClick={() => selectClinic(option.name)}
              >
                <span className="clinic-initial">
                  {option.name.split(' ').map(word => word[0]).join('').substring(0, 2)}
                </span>
                <div className="clinic-details">
                  <span className="clinic-name">{option.name}</span>
                  {option.address && (
                    <div className="clinic-address" style={{ fontSize: '12px', opacity: 0.7 }}>
                      {option.address}
                    </div>
                  )}
                  <div className="clinic-type" style={{ fontSize: '11px', opacity: 0.5, textTransform: 'capitalize' }}>
                    {option.type}
                  </div>
                </div>
              </button>
            ))
          ) : (
            <div className="clinic-option" style={{ opacity: 0.7, padding: '12px' }}>
              {user?.companyName ? 'No additional locations available' : 'No companies or locations found'}
            </div>
          )}
        </div>
      </div>

      <nav className="nav">
        {navigationSections.map((section, index) => (
          <div key={index} className="nav-section">
            {section.title && (
              <div className={`nav-section-title ${isCollapsed ? 'collapsed' : ''}`}>
                {section.title}
              </div>
            )}
            {section.items.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${isCollapsed ? 'collapsed' : ''} ${currentSection === item.id ? 'active' : ''}`}
                onClick={() => handleNavigation(item.id)}
                data-tooltip={item.label}
              >
                <span className={`nav-icon ${item.iconClass}`}></span>
                <span className="nav-label">{item.label}</span>
                {item.badge && <span className="nav-badge">{item.badge}</span>}
              </button>
            ))}
          </div>
        ))}
      </nav>

      <div className={`user-profile ${isCollapsed ? 'collapsed' : ''}`} onClick={handleAccountToggle}>
        <div className="user-avatar">
          {user?.firstName ? user.firstName.charAt(0).toUpperCase() : ''}
          {user?.lastName ? user.lastName.charAt(0).toUpperCase() : ''}
        </div>
        <div className="user-info">
          <div className="user-name">{user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}</div>
          <div className="user-role">{user?.email || 'Administrator'}</div>
        </div>
        <div className="account-arrow">→</div>
      </div>

      {/* Account Dropdown Menu */}
      {showAccountDropdown && (
        <div
          className="account-dropdown"
          style={{
            position: 'fixed',
            bottom: '20px',
            left: isCollapsed ? '90px' : '20px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
            zIndex: 99999,
            minWidth: '280px',
            overflow: 'hidden'
          }}
        >
          {/* User Info Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #f3f4f6',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--medidev-blue), var(--medidev-blue-dark))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {user?.firstName ? user.firstName.charAt(0).toUpperCase() : ''}
                {user?.lastName ? user.lastName.charAt(0).toUpperCase() : ''}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                  {user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {user?.email || 'Administrator'}
                </div>
                {user?.companyName && (
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                    {user.companyName}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Menu Options */}
          <div style={{ padding: '8px 0' }}>
            <button className="account-menu-item" onClick={() => {}}>
              <span className="account-menu-icon">●</span>
              Edit Profile
            </button>
            <button className="account-menu-item" onClick={() => {}}>
              <span className="account-menu-icon">⚙</span>
              Settings
            </button>
            <button className="account-menu-item" onClick={() => {}}>
              <span className="account-menu-icon">●</span>
              Notifications
            </button>
            <button className="account-menu-item" onClick={() => {}}>
              <span className="account-menu-icon">?</span>
              Help & Support
            </button>
            <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '8px 0' }}></div>
            <button
              className="account-menu-item account-menu-logout"
              onClick={() => {
                setShowAccountDropdown(false);
                if (onLogout) {
                  onLogout();
                }
              }}
            >
              <span className="account-menu-icon">×</span>
              Logout
            </button>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default Sidebar;